/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'jquery',
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../PropertiesAPIV2',
	'mobx',
	'underscore'
], ($, APIFactory, PropertiesAPIV2, Mobx, _) => {
	//Delay loading mobx
	//TODO: Remove dependencies on Jquery
	class PropertiesV2 {

		constructor(options) {

			this._api = APIFactory.createAPI(this, [ PropertiesAPIV2 ]);
			this.dashboard = options.features['Dashboard.API'];

			/**
			 * this Properties implementation is been used for both as Dashboard-feature and Content-feature.
			 * this.content will be undefined if this is a dashboard feature instance.
			 */
			this.content = options.content;
			this._providers = [];
			this._propertyList = null;
		}

		initialize() {
			if (this.content) {
				this.content.on('change:state:status', this._updatePropertiesList, this);
			}
		}

		destroy() {
			if (this.content) {
				this.content.off('change:state:status', this._updatePropertiesList, this);
			}
		}

		getAPI() {
			return this._api;
		}

		registerProvider(provider) {
			this._providers.push(provider);
		}

		deregisterProvider(provider) {
			const idx = this._providers.findIndex(p => p === provider);
			if (idx !== -1) {
				this._providers.splice(idx, 1);
			}
		}

		getProviderPropertyNameList() {
			const providerProperties = {};
			let ind = 1;
			this._providers.forEach((provider) => {
				let implType;
				if (provider.getImplType) {
					implType = provider.getImplType();
				} else {
					implType = 'someViewClass_' + ind;
					ind += 1;
				}
				if (!providerProperties[implType]) {
					providerProperties[implType] = [];
				}
				if (provider.getPropertyList) {
					providerProperties[implType].push(...provider.getPropertyList().map(prop => prop.id));
				}
			});
			return providerProperties;
		}

		getPropertyList() {
			// this._propertyList is an mobx observable array. Checking the array falsy is not enough
			if (!this._propertyList || !this._propertyList.length) {
				this._propertyList = this._getPropertiesList();
			}
			return this._propertyList;
		}


		_updatePropertiesList(event) {
			if (event && event.info && event.info.value === 'rendered') {
				if (!this._propertyList || !this._propertyList.length) {
					this._propertyList = this._getPropertiesList();
				} else {
					this._propertyList.replace(this._getPropertiesList());
				}
			}
		}

		_getPropertiesList() {
			const allProps = {};
			const resultProps = [];
			try {
				for (let i = 0; i < this._providers.length; i++) {
					let provider = this._providers[i];
					const properties = provider.getPropertyList ? provider.getPropertyList() : [];
					if (properties) {
						for (let j = 0; j < properties.length; j++) {
							let property = properties[j];
							if (!property.hasOwnProperty('value')) {
								property.value = undefined;
							}
							if (allProps[property.id]) {
								this._mergeProperties(allProps[property.id], property);
							} else {
								allProps[property.id] = property;
							}
						}
					}
				}
			} catch (e) {
				// Do nothing since all error should be logged by render sequence
			}
			this._filterUnsupportedProperties(allProps);
			Object.values(allProps).forEach((entry) => {
				resultProps.push(entry);
			});
			return resultProps;
		}

		_filterUnsupportedProperties(allProps) {
			if (this.dashboard) {
				delete allProps.showTitle;
			}
			return allProps;
		}

		getPropertyLayoutList() {
			const allLayouts = {};
			this._providers.forEach((provider) => {
				const layouts = provider.getPropertyLayoutList ? provider.getPropertyLayoutList() || [] : [];
				layouts.forEach((layout) => {
					if (allLayouts[layout.id]) {
						this._mergeProperties(allLayouts[layout.id], layout);
					} else {
						allLayouts[layout.id] = layout;
					}
				});
			});
			return Object.values(allLayouts);
		}

		_mergeProperties(current, provided) {
			if (provided.override === false) {
				return;
			}
			const currentProfile = current.profile && [...current.profile];

			$.extend(true, current, provided);

			if (currentProfile && provided.profile) {
				current.profile.push(...currentProfile);
				current.profile = _.unique(current.profile);
			}
		}

		modifyPropertyWithCallback(propObj) {
			for (const key in propObj) {
				if (key.indexOf('Callback') !== -1 || key === 'onChange') {
					const defCallback = propObj[key];
					if (typeof defCallback === 'function') {
						return;
					}
					if (typeof defCallback === 'string') {
						/// todo: should not have this case
						console.error('callback in definition should use new property callback method.');
						return;
					}
					try {
						const featurePath = defCallback.feature;
						let feature = null;
						if (featurePath.indexOf('Dashboard.') === 0) {
							const featureName = featurePath.split('.')[1];
							feature = this.dashboard.getFeature(featureName);
						}
						if (!feature) {
							feature = this.content.getFeature(featurePath);
						}
						const params = [this.content];
						params.push(...defCallback.callbackParams);
						propObj[key] = feature[defCallback.callbackFunction].bind(feature, ...params);
					} catch (error) {
						console.error(error);
					}
				} else {
					if (typeof propObj[key] == 'object' && propObj[key] !== null) {
						this.modifyPropertyWithCallback(propObj[key]);
					}
				}
			}
		}

		getPropertyValue(name) {
			let value;
			const property = this.getPropertyList().find(entry => entry.id === name);
			if (property) {
				value = property.value;
				if(value === undefined){
					value = property.getPropertyValue && property.getPropertyValue();
					if(value === undefined){
						value = property.defaultValue;
					}
				}
			}
			return value;
		}

		setPropertyValue(name, value) {
			const property = this.getPropertyList().find(entry => entry.id === name);
			if (property) {
				if(property.setPropertyValue){
					property.setPropertyValue(value);
				}
				property.value = value;
			}
		}
	}

	Mobx.decorate(PropertiesV2, {
		_propertyList: Mobx.observable
	});

	return PropertiesV2;
});
